import type {
  LinkLibraryFolderItem,
  LinkLibraryItem,
  LinkLibraryLink,
} from "../types/content-dashboard/LinkLibrary";

export type {
  LinkLibraryFolderItem,
  LinkLibraryItem,
  LinkLibraryLink,
} from "../types/content-dashboard/LinkLibrary";

export type LibraryFlatRow =
  | { kind: "folder"; name: string }
  | { kind: "link"; name: string; url: string; inFolder: boolean };

export function isHttpUrl(url: string | null | undefined): boolean {
  if (!url || !url.trim()) {
    return false;
  }

  try {
    const parsed = new URL(url.trim());
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export function linkLibraryHasVisibleLinks(items: LinkLibraryItem[]): boolean {
  return items.some((item) =>
    item.kind === "link" ? true : item.links.length > 0
  );
}

export function linkFieldError(link: LinkLibraryLink): string | null {
  if (!link.name.trim()) {
    return "A link needs a name.";
  }

  if (!isHttpUrl(link.url)) {
    return "A link URL must start with http:// or https://.";
  }

  return null;
}

export function linkLibrarySaveError(items: LinkLibraryItem[]): string | null {
  for (const item of items) {
    if (item.kind === "link") {
      const error = linkFieldError(item);
      if (error) {
        return error;
      }
    } else {
      if (!item.name.trim()) {
        return "A folder needs a name.";
      }

      for (const link of item.links) {
        const error = linkFieldError(link);
        if (error) {
          return error;
        }
      }
    }
  }

  return null;
}

export function imageBannerSaveError(banner: {
  imageFilename?: string | null;
  destinationUrl?: string | null;
}): string | null {
  if (!banner.imageFilename || !banner.imageFilename.trim()) {
    return "An image is required.";
  }

  if (banner.destinationUrl && banner.destinationUrl.trim() && !isHttpUrl(banner.destinationUrl)) {
    return "A destination must start with http:// or https://.";
  }

  return null;
}

export function isEmployeeContentWidgetVisible(widget: {
  type: string;
  isHidden?: boolean;
  currentScore?: unknown;
  items?: LinkLibraryItem[];
  imageFilename?: string | null;
}): boolean {
  if (widget.isHidden) {
    return false;
  }

  if (widget.type === "DATA-POINT-SCORE_EMPLOYEE") {
    return widget.currentScore != null;
  }

  if (widget.type === "LINK-LIBRARY") {
    return linkLibraryHasVisibleLinks(widget.items ?? []);
  }

  if (widget.type === "IMAGE-BANNER") {
    return !!widget.imageFilename && widget.imageFilename.trim().length > 0;
  }

  return false;
}

export function flattenLibrary(items: LinkLibraryItem[]): LibraryFlatRow[] {
  const rows: LibraryFlatRow[] = [];

  items.forEach((item) => {
    if (item.kind === "folder") {
      rows.push({ kind: "folder", name: item.name });
      item.links.forEach((link) => {
        rows.push({
          kind: "link",
          name: link.name,
          url: link.url,
          inFolder: true,
        });
      });
    } else {
      rows.push({
        kind: "link",
        name: item.name,
        url: item.url,
        inFolder: false,
      });
    }
  });

  return rows;
}

export function libraryFromFlat(rows: LibraryFlatRow[]): LinkLibraryItem[] {
  const items: LinkLibraryItem[] = [];
  let folder: LinkLibraryFolderItem | null = null;

  rows.forEach((row) => {
    if (row.kind === "folder") {
      folder = { kind: "folder", name: row.name, links: [] };
      items.push(folder);
      return;
    }

    if (row.inFolder && folder) {
      folder.links.push({ name: row.name, url: row.url });
      return;
    }

    folder = null;
    items.push({ kind: "link", name: row.name, url: row.url });
  });

  return items;
}

export function appendRootLink(items: LinkLibraryItem[]): LinkLibraryItem[] {
  return [...items, { kind: "link", name: "", url: "" }];
}

export function appendFolder(items: LinkLibraryItem[]): LinkLibraryItem[] {
  return [...items, { kind: "folder", name: "", links: [] }];
}

function followingChildCount(rows: LibraryFlatRow[], folderIndex: number): number {
  let count = 0;
  for (let index = folderIndex + 1; index < rows.length; index++) {
    const row = rows[index];
    if (row.kind === "link" && row.inFolder) {
      count++;
    } else {
      break;
    }
  }
  return count;
}

export function moveLibraryRow(
  items: LinkLibraryItem[],
  fromIndex: number,
  toIndex: number
): LinkLibraryItem[] {
  const rows = flattenLibrary(items);
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= rows.length ||
    toIndex >= rows.length ||
    fromIndex === toIndex
  ) {
    return items;
  }

  const moving = rows[fromIndex];
  const blockLength =
    moving.kind === "folder" ? 1 + followingChildCount(rows, fromIndex) : 1;

  if (toIndex > fromIndex && toIndex < fromIndex + blockLength) {
    return items;
  }

  const next = rows.slice();
  const block = next.splice(fromIndex, blockLength);
  let insertAt = toIndex > fromIndex ? toIndex - blockLength + 1 : toIndex;

  if (moving.kind === "link") {
    const link = block[0];
    if (link.kind !== "link") {
      return items;
    }

    const target = rows[toIndex];
    if (target.kind === "folder") {
      const folderIndex = next.indexOf(target);
      link.inFolder = true;
      next.splice(folderIndex + 1, 0, link);
      return libraryFromFlat(next);
    }

    const above = insertAt > 0 ? next[insertAt - 1] : undefined;
    link.inFolder = !!above && (above.kind === "folder" || (above.kind === "link" && above.inFolder));
    next.splice(insertAt, 0, link);
    return libraryFromFlat(next);
  }

  while (insertAt < next.length) {
    const row = next[insertAt];
    if (row.kind === "link" && row.inFolder) {
      insertAt++;
    } else {
      break;
    }
  }

  next.splice(insertAt, 0, ...block);
  return libraryFromFlat(next);
}

export function updateLibraryRow(
  items: LinkLibraryItem[],
  flatIndex: number,
  patch: { name?: string; url?: string }
): LinkLibraryItem[] {
  const rows = flattenLibrary(items);
  const row = rows[flatIndex];
  if (!row) {
    return items;
  }

  if (row.kind === "folder") {
    rows[flatIndex] = { kind: "folder", name: patch.name ?? row.name };
  } else {
    rows[flatIndex] = {
      kind: "link",
      name: patch.name ?? row.name,
      url: patch.url ?? row.url,
      inFolder: row.inFolder,
    };
  }

  return libraryFromFlat(rows);
}

export function removeLibraryRow(
  items: LinkLibraryItem[],
  flatIndex: number
): LinkLibraryItem[] {
  const rows = flattenLibrary(items);
  const row = rows[flatIndex];
  if (!row) {
    return items;
  }

  const blockLength =
    row.kind === "folder" ? 1 + followingChildCount(rows, flatIndex) : 1;
  rows.splice(flatIndex, blockLength);
  return libraryFromFlat(rows);
}

export function visibleLibraryItems(items: LinkLibraryItem[]): LinkLibraryItem[] {
  const visible: LinkLibraryItem[] = [];
  items.forEach((item) => {
    if (item.kind === "link") {
      visible.push(item);
      return;
    }

    if (item.links.length > 0) {
      visible.push(item);
    }
  });
  return visible;
}
